import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useApolloClient } from '@apollo/client';

import { JoinLessonNowDocument } from 'core/graphql';
import type { JoinLessonNowMutation, JoinLessonNowMutationVariables } from 'core/graphql';
import { authState, IAuthState } from 'core/state';

import {
  classifyJoinCustomErrors,
  classifyJoinLessonError,
  JoinLessonErrorKind,
} from './transform';

export type JoinLessonResultType = {
  success: boolean;
  redirectUrl?: string | null;
  /** Причина отказа; `null` при успехе. */
  error: JoinLessonErrorKind | null;
};

type JoinLessonType = [
  (groupLessonId: number) => Promise<JoinLessonResultType>,
  { loading: boolean },
];

/** Сколько ждать, пока App обновит токен, прежде чем сдаться. */
const TOKEN_REFRESH_WAIT_MS = 15_000;

/**
 * Ждём свежий access-токен после «Signature has expired».
 *
 * Обновляет токен не этот хук: errorLink ставит `isTokenRefreshed: false`, и
 * App сам гонит refresh (страница на это время уходит в Splash и
 * перемонтируется — поэтому повтор живёт в замыкании промиса, а не в состоянии
 * компонента). `true` — токен новый, можно повторить; `false` — разлогинили
 * («Error decoding signature» / refresh не прошёл) или не дождались.
 */
export const waitForFreshToken = (
  staleToken: string | null,
  timeoutMs = TOKEN_REFRESH_WAIT_MS,
): Promise<boolean> =>
  new Promise((resolve) => {
    let done = false;
    let unsubscribe: (() => void) | null = null;
    const finish = (ok: boolean) => {
      if (done) {
        return;
      }
      done = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve(ok);
    };
    const check = (state: IAuthState) => {
      if (done) {
        return;
      }
      if (!state.isLoggedIn) {
        finish(false);
        return;
      }
      if (state.isTokenRefreshed && state.access_token && state.access_token !== staleToken) {
        finish(true);
        return;
      }
      unsubscribe = authState.onNextChange(check);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    check(authState());
  });

/**
 * Входы «в полёте» — НА УРОВНЕ МОДУЛЯ, по `groupLessonId`.
 *
 * Флаг `loading` в состоянии компонента не переживает перемонтирования: пока
 * App обновляет токен, страница уходит в Splash и монтируется заново с
 * `loading=false`, а первая попытка (с повтором) ещё идёт в замыкании промиса.
 * Клик в это окно открыл бы вторую вкладку и записал бы второй вход. Реестр
 * живёт дольше компонента: второй вызов для того же занятия получает тот же
 * промис (мутация не повторяется), а новый экземпляр сразу видит «занято».
 */
const inFlightJoins = new Map<number, Promise<JoinLessonResultType>>();
const inFlightListeners = new Set<() => void>();

const notifyInFlight = () => {
  inFlightListeners.forEach((listener) => listener());
};

/** Идёт ли сейчас вход на это занятие (в любом экземпляре компонента). */
export const isJoinInFlight = (groupLessonId: number): boolean => inFlightJoins.has(groupLessonId);

/** Подписка на изменения реестра входов; возвращает отписку. */
export const subscribeJoinInFlight = (listener: () => void): (() => void) => {
  inFlightListeners.add(listener);
  return () => {
    inFlightListeners.delete(listener);
  };
};

/**
 * Spec 0013: records the join click on the backend and returns the redirect
 * URL. The join URL is never available through queries — only from this
 * mutation.
 *
 * Never rejects: every failure comes back as `error` with its reason, so the
 * caller can tell the student WHY. An expired token is retried exactly once,
 * after the app refreshed it.
 *
 * One join per lesson at a time across ALL instances: a second call while one
 * is in flight returns the same promise without calling the mutation again.
 * `loading` comes from that module-level registry — for `watchLessonId` when
 * given, otherwise for any lesson — so a remounted component is busy at once
 * and is released when the promise settles.
 *
 * Мутация идёт через `client.mutate`, а НЕ через `useJoinLessonNowMutation`:
 * вход (с повтором) живёт дольше экземпляра, который его начал, — на время
 * refresh токена App уходит в Splash и размонтирует страницу. `execute` из
 * `useMutation` (Apollo 3.5) пишет `loading: true` в состояние СВОЕГО
 * компонента без проверки, смонтирован ли он, — повтор из размонтированного
 * экземпляра давал «state update on an unmounted component». У клиента
 * состояния компонента нет, а `loading` и так берётся из реестра.
 */
export const useJoinLesson = (watchLessonId?: number): JoinLessonType => {
  const client = useApolloClient();
  const [, rerender] = useReducer((x: number) => x + 1, 0);

  const loading =
    watchLessonId !== undefined && !Number.isNaN(watchLessonId)
      ? isJoinInFlight(watchLessonId)
      : inFlightJoins.size > 0;
  const renderedLoading = useRef(loading);
  renderedLoading.current = loading;

  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeJoinInFlight(() => {
      if (active) {
        rerender();
      }
    });
    // Реестр мог смениться между рендером и подпиской.
    const now =
      watchLessonId !== undefined && !Number.isNaN(watchLessonId)
        ? isJoinInFlight(watchLessonId)
        : inFlightJoins.size > 0;
    if (now !== renderedLoading.current) {
      rerender();
    }
    return () => {
      active = false;
      unsubscribe();
    };
  }, [watchLessonId]);

  const attempt = useCallback(
    async (groupLessonId: number): Promise<JoinLessonResultType> => {
      try {
        const { data } = await client.mutate<JoinLessonNowMutation, JoinLessonNowMutationVariables>(
          { mutation: JoinLessonNowDocument, variables: { groupLessonId } },
        );
        const payload = data?.lmsJoinLesson;
        if (payload?.success && payload?.redirectUrl) {
          return { success: true, redirectUrl: payload.redirectUrl, error: null };
        }
        return {
          success: false,
          redirectUrl: null,
          error: classifyJoinCustomErrors(payload?.customErrors),
        };
      } catch (e) {
        return { success: false, redirectUrl: null, error: classifyJoinLessonError(e) };
      }
    },
    [client],
  );

  const run = useCallback(
    async (groupLessonId: number): Promise<JoinLessonResultType> => {
      const usedToken = authState().access_token;
      const first = await attempt(groupLessonId);
      if (first.error !== JoinLessonErrorKind.token) {
        return first;
      }
      const refreshed = await waitForFreshToken(usedToken);
      if (!refreshed) {
        return { ...first, error: JoinLessonErrorKind.sessionExpired };
      }
      const second = await attempt(groupLessonId);
      // Второй раз токен не чиним — это уже не «протух», а разлогин.
      return second.error === JoinLessonErrorKind.token
        ? { ...second, error: JoinLessonErrorKind.sessionExpired }
        : second;
    },
    [attempt],
  );

  const joinLesson = useCallback(
    (groupLessonId: number): Promise<JoinLessonResultType> => {
      const pending = inFlightJoins.get(groupLessonId);
      if (pending) {
        return pending;
      }
      const tracked: Promise<JoinLessonResultType> = run(groupLessonId)
        .catch(
          (): JoinLessonResultType => ({
            success: false,
            redirectUrl: null,
            error: JoinLessonErrorKind.unknown,
          }),
        )
        .finally(() => {
          if (inFlightJoins.get(groupLessonId) === tracked) {
            inFlightJoins.delete(groupLessonId);
            notifyInFlight();
          }
        });
      inFlightJoins.set(groupLessonId, tracked);
      notifyInFlight();
      return tracked;
    },
    [run],
  );

  return [joinLesson, { loading }];
};
